import { api } from '@/config/api';
import type { AiBotConfig } from '@/services/whatsapp-account.service';

export type WebchatPreChat = 'none' | 'name_phone' | 'name_email' | 'name_phone_or_email';

export interface AiAgentWebchat {
  enabled: boolean;
  widgetKey: string;
  allowedDomains: string[];
  title?: string;
  greeting?: string;
  color: string;
  position: 'right' | 'left';
  preChat: WebchatPreChat;
  logoUrl?: string;
}

export interface AiAgent {
  agentUid: string;
  name: string;
  brainAccountUid?: string;
  isActive: boolean;
  apiEnabled: boolean;
  webchat: AiAgentWebchat;
  /** Absent on older agents = all sources. */
  knowledge?: { mode: 'off' | 'all' | 'selected'; sourceUids: string[] };
  /** Its own setup — used when it doesn't borrow a WhatsApp number's (`brainAccountUid`). */
  persona?: AiBotConfig;
  handoffKeywords?: string[];
  /** Only on the list: WhatsApp numbers this agent answers. */
  whatsappNumbers?: number;
  stats: { conversations?: number; messages?: number; lastMessageAt?: string };
  createdAt?: string;
  /** Only on the list: the WhatsApp number whose AI setup the agent answers with. */
  brain?: { accountUid: string; businessName: string; phone: string } | null;
}

export type AiChannel = 'web' | 'api';
export type AiPauseReason = 'handoff' | 'agent_reply' | 'turn_cap' | 'manual' | 'ai_failed';

export interface AiConversation {
  conversationUid: string;
  agentUid: string;
  channel: AiChannel;
  externalKey?: string;
  visitor: { name?: string; phone?: string; email?: string; pageUrl?: string; referrer?: string; userAgent?: string };
  leadUid?: string;
  status: 'open' | 'closed';
  aiPaused: boolean;
  pausedReason?: AiPauseReason;
  lastError?: string;
  collectedFields: Record<string, string>;
  lastMessageAt?: string;
  lastMessagePreview?: string;
  messageCount: number;
  unreadForTeam: number;
  createdAt?: string;
}

export interface AiMessage {
  messageUid: string;
  role: 'customer' | 'assistant' | 'agent' | 'system';
  body: string;
  source?: string;
  action?: string;
  authorUserUid?: string;
  sources?: Array<{ sourceUid: string; title: string; url?: string }>;
  credits?: number;
  createdAt: string;
}

export interface AgentAnalytics {
  days: number;
  totals: {
    conversations: number; web: number; api: number; leads: number; aiReplies: number; credits: number;
    handoffs: number; takeovers: number; qualified: number; resolvedByAi: number; resolutionRate: number; aiFailed: number; unanswered: number; whatsappNumbers: number;
  };
  series: Array<{ date: string; conversations: number; handoffs: number; resolved: number }>;
}

export interface WhatsappChannel {
  accountUid: string;
  businessName: string;
  phone: string;
  botMode: 'flow' | 'ai_chat' | 'off';
  isActive: boolean;
  assigned: boolean;
  otherAgent: { agentUid: string; name: string } | null;
}

export type ConversationFilter = { agentUid?: string; status?: 'open' | 'closed' | 'needs_team'; channel?: AiChannel; before?: string };

export type AgentPatch = Partial<Pick<AiAgent, 'name' | 'isActive' | 'apiEnabled' | 'knowledge' | 'persona' | 'handoffKeywords'>> & {
  brainAccountUid?: string;
  webchat?: Partial<Omit<AiAgentWebchat, 'widgetKey'>>;
};

const BASE = '/slighter_crm/ai-agents';
const CONV = '/slighter_crm/ai-conversations';

/** Where customers' sites load the chat from — the same API the CRM talks to. */
export const PUBLIC_API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');

export const aiAgentsService = {
  list: () => api.get<AiAgent[]>(BASE).then((r) => r.data),
  get: (agentUid: string) => api.get<AiAgent>(`${BASE}/${agentUid}`).then((r) => r.data),
  /** For pages outside AI Agents: a missing agent or module is a normal answer, not an error toast. */
  getQuiet: (agentUid: string) => api.get<AiAgent>(`${BASE}/${agentUid}`, { expectedStatuses: [403, 404] }).then((r) => r.data).catch(() => null),
  create: (body: { name: string; brainAccountUid?: string }) => api.post<AiAgent>(BASE, body).then((r) => r.data),
  update: (agentUid: string, body: AgentPatch) => api.patch<AiAgent>(`${BASE}/${agentUid}`, body).then((r) => r.data),
  remove: (agentUid: string) => api.delete<{ ok: boolean }>(`${BASE}/${agentUid}`).then((r) => r.data),
  regenerateKey: (agentUid: string) => api.post<AiAgent>(`${BASE}/${agentUid}/regenerate-key`).then((r) => r.data),
  analytics: (agentUid: string, days = 30) => api.get<AgentAnalytics>(`${BASE}/${agentUid}/analytics`, { params: { days } }).then((r) => r.data),
  whatsappChannels: (agentUid: string) => api.get<WhatsappChannel[]>(`${BASE}/${agentUid}/channels/whatsapp`).then((r) => r.data),
  setWhatsappChannel: (agentUid: string, accountUid: string, enabled: boolean) =>
    api.post<WhatsappChannel[]>(`${BASE}/${agentUid}/channels/whatsapp`, { accountUid, enabled }).then((r) => r.data),

  conversations: (q: ConversationFilter = {}) => api.get<AiConversation[]>(CONV, { params: q }).then((r) => r.data),
  conversation: (uid: string) => api.get<{ conversation: AiConversation; messages: AiMessage[] }>(`${CONV}/${uid}`).then((r) => r.data),
  reply: (uid: string, text: string) => api.post<AiMessage>(`${CONV}/${uid}/reply`, { text }).then((r) => r.data),
  setAiPaused: (uid: string, paused: boolean) => api.post<AiConversation>(`${CONV}/${uid}/ai`, { paused }).then((r) => r.data),
  setStatus: (uid: string, status: 'open' | 'closed') => api.post<AiConversation>(`${CONV}/${uid}/status`, { status }).then((r) => r.data),
};

export function embedSnippet(widgetKey: string): string {
  return `<script src="${PUBLIC_API_BASE}/public/webchat/widget.js?key=${widgetKey}" async></script>`;
}

export function frameUrl(widgetKey: string): string {
  return `${PUBLIC_API_BASE}/public/webchat/frame?key=${widgetKey}`;
}

export const PRE_CHAT_OPTIONS: Array<{ value: WebchatPreChat; label: string; description: string }> = [
  { value: 'name_phone_or_email', label: 'Name + phone or email', description: 'Recommended — every chat becomes a lead you can follow up' },
  { value: 'name_phone', label: 'Name + phone', description: 'Best when your team follows up on WhatsApp' },
  { value: 'name_email', label: 'Name + email', description: 'Best for B2B and longer sales cycles' },
  { value: 'none', label: 'No form — chat straight away', description: 'More chats; a lead is created once the visitor shares a phone or email' },
];

export const PAUSE_REASON_LABEL: Record<AiPauseReason, string> = {
  handoff: 'The AI handed this chat to your team',
  agent_reply: 'A team member took over',
  turn_cap: 'The AI hit its hourly turn limit',
  manual: 'AI paused by your team',
  ai_failed: 'The AI could not reply',
};
